import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { getQuickThemeOptions } from '../utils/mailSettings';
import {
  isPushSupported,
  isSubscribed,
  subscribeToPush,
  unsubscribeFromPush,
} from '../utils/pushNotifications';
import ThemePickerModal from './ThemePickerModal';
import '../styles/wallpapers.css';
import './QuickSettings.css';

const QUICK_THEME_OPTIONS = getQuickThemeOptions();

const DENSITY_OPTIONS = [
  { value: 'default', label: 'Default', preview: 'density-default' },
  { value: 'comfortable', label: 'Comfortable', preview: 'density-comfortable' },
  { value: 'compact', label: 'Compact', preview: 'density-compact' },
];

const INBOX_OPTIONS = [
  { value: 'default', label: 'Default', preview: 'inbox-default', customize: true },
  { value: 'important', label: 'Important first', preview: 'inbox-important' },
  { value: 'unread', label: 'Unread first', preview: 'inbox-unread' },
  { value: 'starred', label: 'Starred first', preview: 'inbox-starred' },
  { value: 'priority', label: 'Priority Inbox', preview: 'inbox-priority', customize: true },
  { value: 'multiple', label: 'Multiple Inboxes', preview: 'inbox-multiple', customize: true },
];

const READING_PANE_OPTIONS = [
  { value: 'no-split', label: 'No split', preview: 'pane-none' },
  { value: 'right', label: 'Right of inbox', preview: 'pane-right' },
  { value: 'below', label: 'Below inbox', preview: 'pane-below' },
];

const LayoutPreview = ({ type }) => (
  <span className={`qs-preview qs-preview-${type}`} aria-hidden="true" />
);

const RadioRow = ({ name, value, selected, label, preview, customize, onSelect, onCustomize }) => (
  <label className={`qs-radio-row ${selected ? 'selected' : ''}`}>
    <input
      type="radio"
      name={name}
      value={value}
      checked={selected}
      onChange={() => onSelect(value)}
    />
    <span className="qs-radio-dot" />
    <span className="qs-radio-label-wrap">
      <span className="qs-radio-label">{label}</span>
      {customize && (
        <button
          type="button"
          className="qs-customize-link"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onCustomize?.(value);
          }}
        >
          Customize
        </button>
      )}
    </span>
    <LayoutPreview type={preview} />
  </label>
);

const QuickSettings = ({
  open,
  onClose,
  overlayRef,
  settings,
  onSettingChange,
  onThemeSelect,
  onThemePreview,
  onReset,
}) => {
  const [showThemePicker, setShowThemePicker] = useState(false);
  const pushSupported = isPushSupported();
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushDenied, setPushDenied] = useState(
    pushSupported && typeof Notification !== 'undefined' && Notification.permission === 'denied'
  );

  useEffect(() => {
    let cancelled = false;
    if (open && pushSupported) {
      isSubscribed().then((active) => {
        if (!cancelled) setPushOn(active);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [open, pushSupported]);

  const handleTogglePush = async () => {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      if (pushOn) {
        await unsubscribeFromPush();
        setPushOn(false);
      } else {
        const ok = await subscribeToPush({ requestPermission: true });
        setPushOn(ok);
        setPushDenied(typeof Notification !== 'undefined' && Notification.permission === 'denied');
      }
    } finally {
      setPushBusy(false);
    }
  };

  useEffect(() => {
    if (!open && !showThemePicker) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, showThemePicker]);

  if (!open && !showThemePicker) return null;

  const isThemeSelected = (opt) =>
    (settings.themeId && settings.themeId === opt.id) ||
    (settings.theme === opt.theme && (settings.appearance || 'default') === opt.appearance);

  const handleInboxSelect = (value) => {
    onSettingChange('inboxType', value);
  };

  const getInboxChecked = (optValue) => settings.inboxType === optValue;

  return createPortal(
    <>
    {open && (
    <div
      className="quick-settings-overlay"
      ref={overlayRef}
      onClick={onClose}
      role="presentation"
    >
      <aside
        className="quick-settings-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Quick settings"
      >
        <header className="quick-settings-header">
          <h2 className="quick-settings-title">Quick settings</h2>
          <button type="button" className="quick-settings-close" onClick={onClose} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </header>

        <div className="quick-settings-body">
          <section className="qs-section">
            <h3 className="qs-section-title">Density</h3>
            <div className="qs-radio-list">
              {DENSITY_OPTIONS.map((opt) => (
                <RadioRow
                  key={opt.value}
                  name="density"
                  value={opt.value}
                  selected={settings.density === opt.value}
                  label={opt.label}
                  preview={opt.preview}
                  onSelect={(v) => onSettingChange('density', v)}
                />
              ))}
            </div>
          </section>

          <hr className="qs-divider" />

          <section className="qs-section">
            <div className="qs-section-head">
              <h3 className="qs-section-title">Theme</h3>
              <button type="button" className="qs-view-all" onClick={() => setShowThemePicker(true)}>
                View all
              </button>
            </div>
            <div className="qs-theme-grid">
              {QUICK_THEME_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  className={`qs-theme-thumb ${opt.className} ${isThemeSelected(opt) ? 'selected' : ''}`}
                  onClick={() => onThemeSelect(opt)}
                  aria-label={`Theme ${opt.id}`}
                  aria-pressed={isThemeSelected(opt)}
                />
              ))}
            </div>
          </section>

          <hr className="qs-divider" />

          <section className="qs-section">
            <h3 className="qs-section-title">Inbox type</h3>
            <div className="qs-radio-list">
              {INBOX_OPTIONS.map((opt) => (
                <RadioRow
                  key={opt.value}
                  name="inboxType"
                  value={opt.value}
                  selected={getInboxChecked(opt.value)}
                  label={opt.label}
                  preview={opt.preview}
                  customize={opt.customize}
                  onSelect={handleInboxSelect}
                  onCustomize={() => {}}
                />
              ))}
            </div>
          </section>

          <hr className="qs-divider" />

          <section className="qs-section">
            <h3 className="qs-section-title">Notifications</h3>
            {pushSupported ? (
              <>
                <label className="qs-toggle-row">
                  <span className="qs-toggle-label-wrap">
                    <span className="qs-radio-label">Desktop notifications</span>
                    <span className="qs-toggle-hint">
                      {pushDenied
                        ? 'Blocked in browser settings — allow notifications for this site to enable.'
                        : 'Get an alert when a new mail arrives, even when the app is closed.'}
                    </span>
                  </span>
                  <button
                    type="button"
                    className={`qs-switch ${pushOn ? 'on' : ''}`}
                    role="switch"
                    aria-checked={pushOn}
                    aria-label="Toggle desktop notifications"
                    disabled={pushBusy || pushDenied}
                    onClick={handleTogglePush}
                  >
                    <span className="qs-switch-knob" />
                  </button>
                </label>
              </>
            ) : (
              <p className="qs-toggle-hint">Notifications are not supported in this browser.</p>
            )}
          </section>

          <hr className="qs-divider" />

          <section className="qs-section">
            <h3 className="qs-section-title">Reading pane</h3>
            <div className="qs-radio-list">
              {READING_PANE_OPTIONS.map((opt) => (
                <RadioRow
                  key={opt.value}
                  name="readingPane"
                  value={opt.value}
                  selected={settings.readingPane === opt.value}
                  label={opt.label}
                  preview={opt.preview}
                  onSelect={(v) => onSettingChange('readingPane', v)}
                />
              ))}
            </div>
          </section>
        </div>

        <footer className="quick-settings-footer">
          <button type="button" className="qs-reset-btn" onClick={onReset}>
            Reset all settings
          </button>
        </footer>
      </aside>
    </div>
    )}

    <ThemePickerModal
      open={showThemePicker}
      onClose={() => setShowThemePicker(false)}
      settings={settings}
      onPreview={onThemePreview}
      onSave={onThemeSelect}
    />
    </>,
    document.body
  );
};

export default QuickSettings;
